/**
 * Staff Details - Detail Modal Component
 * Displays full staff information in a modal view
 */
import { 
  User, Phone, Briefcase, Heart, GraduationCap, Brain, X, 
  AlertCircle, Lightbulb 
} from 'lucide-react';
import { SectionHeader } from './FormComponents';
import { StaffQuickGuide, MeetingGuideGenerator } from './StaffQuickGuide';
import { VAKD_TYPES, DOPE_TYPES, COLOUR_BRAIN_TYPES } from './constants';

const DetailModal = ({ staff, detail, onClose }) => {
  if (!staff) return null;
  
  const personality = detail?.personality || {};
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="staff-detail-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-3xl">
              {staff.name?.charAt(0) || 'S'}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white">{staff.name}</h2>
              <p className="text-blue-200">{detail?.position || staff.position || 'Tiada Jawatan'}</p>
              <p className="text-blue-300 text-sm">{detail?.department || staff.department || 'Tiada Jabatan'}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="close-detail-modal">
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {!detail ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-600">Maklumat lengkap belum diisi untuk staff ini.</p>
            </div>
          ) : (
            <>
              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <SectionHeader icon={User} title="Maklumat Peribadi" />
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">IC:</span> {detail.ic_number || '-'}</p>
                    <p><span className="text-gray-500">Tarikh Lahir:</span> {detail.date_of_birth || '-'}</p>
                    <p><span className="text-gray-500">Jantina:</span> {detail.gender || '-'}</p>
                    <p><span className="text-gray-500">Status Perkahwinan:</span> {detail.marital_status || '-'}</p>
                    <p><span className="text-gray-500">Bangsa:</span> {detail.race || '-'}</p>
                    <p><span className="text-gray-500">Agama:</span> {detail.religion || '-'}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <SectionHeader icon={Phone} title="Maklumat Perhubungan" color="green" />
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Telefon:</span> {detail.phone || '-'}</p>
                    <p><span className="text-gray-500">Email:</span> {detail.email || '-'}</p>
                    <p><span className="text-gray-500">Alamat:</span> {detail.address || '-'}</p>
                    <p><span className="text-gray-500">Bandar:</span> {detail.city || '-'}</p>
                    <p><span className="text-gray-500">Negeri:</span> {detail.state || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Employment & Emergency */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <SectionHeader icon={Briefcase} title="Maklumat Pekerjaan" color="purple" />
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">ID Pekerja:</span> {detail.employee_id || '-'}</p>
                    <p><span className="text-gray-500">Jenis Pekerjaan:</span> {detail.employment_type || '-'}</p>
                    <p><span className="text-gray-500">Tarikh Mula:</span> {detail.join_date || '-'}</p>
                    <p><span className="text-gray-500">Tarikh Pengesahan:</span> {detail.confirmation_date || '-'}</p>
                    <p><span className="text-gray-500">Bank:</span> {detail.bank_name || '-'}</p>
                    <p><span className="text-gray-500">No. Akaun:</span> {detail.bank_account || '-'}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <SectionHeader icon={Heart} title="Kecemasan" color="red" />
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Nama:</span> {detail.emergency_contact?.name || '-'}</p>
                    <p><span className="text-gray-500">Hubungan:</span> {detail.emergency_contact?.relationship || '-'}</p>
                    <p><span className="text-gray-500">Telefon:</span> {detail.emergency_contact?.phone || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Education */}
              <div className="bg-gray-50 rounded-xl p-4">
                <SectionHeader icon={GraduationCap} title="Pendidikan & Kelayakan" color="amber" />
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Tahap Pendidikan:</span> {detail.education_level || '-'}</p>
                    <p><span className="text-gray-500">Bidang:</span> {detail.education_field || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Bahasa:</p>
                    <div className="flex flex-wrap gap-1">
                      {(detail.languages || []).map(lang => (
                        <span key={lang} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">{lang}</span>
                      ))}
                      {(!detail.languages || detail.languages.length === 0) && <span className="text-gray-400 text-sm">-</span>}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Personality */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4">
                <SectionHeader icon={Brain} title="Penilaian Personaliti" color="purple" />
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Gaya Komunikasi</p>
                    <p className="font-medium">{personality.communication_style || '-'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Gaya Kerja</p>
                    <p className="font-medium">{personality.work_style || '-'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Potensi Kepimpinan</p>
                    <p className={`font-medium ${
                      personality.leadership_potential === 'High' ? 'text-green-600' :
                      personality.leadership_potential === 'Medium' ? 'text-blue-600' : 'text-gray-600'
                    }`}>{personality.leadership_potential || '-'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Penyelesaian Konflik</p>
                    <p className="font-medium">{personality.conflict_resolution || '-'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Pengendalian Tekanan</p>
                    <p className="font-medium">{personality.stress_handling || '-'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Jenis Motivasi</p>
                    <p className="font-medium">{personality.motivation_type || '-'}</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-500 mb-1">Peranan Dalam Pasukan</p>
                  <p className="font-medium text-lg">{personality.team_role || '-'}</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Kekuatan</p>
                    <div className="flex flex-wrap gap-1">
                      {(personality.strengths || []).map(s => (
                        <span key={s} className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">{s}</span>
                      ))}
                      {(!personality.strengths || personality.strengths.length === 0) && <span className="text-gray-400 text-sm">-</span>}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Perlu Diperbaiki</p>
                    <div className="flex flex-wrap gap-1">
                      {(personality.areas_for_improvement || []).map(s => (
                        <span key={s} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">{s}</span>
                      ))}
                      {(!personality.areas_for_improvement || personality.areas_for_improvement.length === 0) && <span className="text-gray-400 text-sm">-</span>}
                    </div>
                  </div>
                </div>
                
                {personality.notes && (
                  <div className="mt-3 p-3 bg-white rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Nota Personaliti</p>
                    <p className="text-sm">{personality.notes}</p>
                  </div>
                )}
              </div>
              
              {/* VAKD & DOPE Section */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* VAKD */}
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-cyan-200">
                    <span className="text-xl">🧠</span>
                    <h3 className="font-semibold text-cyan-800">VAKD - Cara Terima Maklumat</h3>
                  </div>
                  {personality.vakd_type ? (
                    <>
                      <div className="bg-white rounded-lg p-4 mb-3">
                        <p className="text-2xl mb-1">
                          {personality.vakd_type === 'Visual' && '👁️'}
                          {personality.vakd_type === 'Audio' && '👂'}
                          {personality.vakd_type === 'Kinestetik' && '✋'}
                          {personality.vakd_type === 'Digital' && '🧠'}
                          <span className="ml-2 font-bold text-cyan-800">{personality.vakd_type}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          {VAKD_TYPES.find(v => v.value === personality.vakd_type)?.desc || ''}
                        </p>
                      </div>
                      {personality.vakd_notes && (
                        <div className="text-sm text-gray-600 bg-white rounded-lg p-2">
                          <p className="text-xs text-gray-500 mb-1">Nota:</p>
                          {personality.vakd_notes}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm">Belum dinilai</p>
                  )}
                </div>
                
                {/* DOPE */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-amber-200">
                    <span className="text-xl">🎯</span>
                    <h3 className="font-semibold text-amber-800">DOPE - Cara Buat Keputusan</h3>
                  </div>
                  {personality.dope_type ? (
                    <>
                      <div className="bg-white rounded-lg p-4 mb-3">
                        <p className="text-2xl mb-1">
                          {personality.dope_type === 'Dove' && '🕊️'}
                          {personality.dope_type === 'Owl' && '🦉'}
                          {personality.dope_type === 'Peacock' && '🦚'}
                          {personality.dope_type === 'Eagle' && '🦅'}
                          <span className="ml-2 font-bold text-amber-800">{personality.dope_type}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          {DOPE_TYPES.find(d => d.value === personality.dope_type)?.desc || ''}
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          <strong>Sesuai:</strong> {DOPE_TYPES.find(d => d.value === personality.dope_type)?.role || ''}
                        </p>
                      </div>
                      {personality.dope_notes && (
                        <div className="text-sm text-gray-600 bg-white rounded-lg p-2">
                          <p className="text-xs text-gray-500 mb-1">Nota:</p>
                          {personality.dope_notes}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm">Belum dinilai</p>
                  )}
                </div>
              </div>
              
              {/* Colour Brain */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-purple-200">
                  <span className="text-xl">🎨</span>
                  <h3 className="font-semibold text-purple-800">Colour Brain - Cara Berfikir</h3>
                </div>
                {personality.colour_brain ? (
                  <>
                    <div className={`rounded-lg p-4 mb-3 bg-gradient-to-br ${
                      COLOUR_BRAIN_TYPES.find(c => c.value === personality.colour_brain)?.bgColor || 'from-gray-500 to-gray-600'
                    } text-white`}>
                      <p className="text-2xl mb-1">
                        {personality.colour_brain === 'Red' && '🔴'}
                        {personality.colour_brain === 'Blue' && '🔵'}
                        {personality.colour_brain === 'Green' && '🟢'}
                        {personality.colour_brain === 'Yellow' && '🟡'}
                        <span className="ml-2 font-bold">{personality.colour_brain} Brain</span>
                        {personality.colour_brain_secondary && (
                          <span className="ml-2 text-lg opacity-80">+ {personality.colour_brain_secondary}</span>
                        )}
                      </p>
                      <p className="text-sm opacity-90">
                        {COLOUR_BRAIN_TYPES.find(c => c.value === personality.colour_brain)?.desc || ''}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(COLOUR_BRAIN_TYPES.find(c => c.value === personality.colour_brain)?.traits || []).map((trait, i) => (
                          <span key={i} className="px-2 py-0.5 bg-white/20 rounded text-xs">{trait}</span>
                        ))}
                      </div>
                      <p className="text-xs mt-2 opacity-80">
                        <strong>Sesuai:</strong> {COLOUR_BRAIN_TYPES.find(c => c.value === personality.colour_brain)?.role || ''}
                      </p>
                    </div>
                    {personality.colour_brain_notes && (
                      <div className="text-sm text-gray-600 bg-white rounded-lg p-2">
                        <p className="text-xs text-gray-500 mb-1">Nota:</p>
                        {personality.colour_brain_notes}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400 text-sm">Belum dinilai</p>
                )}
              </div>
              
              {/* Leader's Quick Guide */}
              {(personality.vakd_type || personality.dope_type) && (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-emerald-800">Leader&apos;s Quick Guide - Cara Handle Staff Ini</h3>
                  </div>
                  <StaffQuickGuide staff={staff} detail={detail} />
                </div>
              )}
              
              {/* 1-on-1 Meeting Guide */}
              {personality.dope_type && (
                <MeetingGuideGenerator staff={staff} detail={detail} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailModal;
